import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { THEME_CHOICES, resolveTheme, type ThemeChoice, type ThemeName } from '@whinerp/ui';

/**
 * Light, dark, or whatever the operating system says.
 *
 * Three states rather than two: "match system" is a real choice and collapsing
 * it into a boolean means somebody who switches their laptop to dark at sunset
 * has to switch the product too.
 *
 * The choice sets `data-theme` on the document element, which is what the
 * generated stylesheet keys off. Nothing else in the application knows which
 * theme it is in.
 */

export interface ThemeContextValue {
  choice: ThemeChoice;
  resolved: ThemeName;
  setChoice: (choice: ThemeChoice) => void;
  choices: readonly ThemeChoice[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ThemeProvider({
  choice = 'system',
  onChange,
  children,
}: {
  choice?: ThemeChoice;
  onChange?: (choice: ThemeChoice) => void;
  children: ReactNode;
}): JSX.Element {
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const listen = (event: MediaQueryListEvent): void => setSystemDark(event.matches);
    query.addEventListener('change', listen);
    return () => query.removeEventListener('change', listen);
  }, []);

  const resolved = resolveTheme(choice, systemDark);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    // "system" removes the attribute entirely rather than writing the resolved
    // value, so the stylesheet's own media query stays in charge - which is
    // what keeps it correct if the system changes before React notices.
    if (choice === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', choice);
  }, [choice]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      choice,
      resolved,
      setChoice: (next) => onChange?.(next),
      choices: THEME_CHOICES,
    }),
    [choice, resolved, onChange],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme was called outside a ThemeProvider.');
  return context;
}
